import { getSerializedNamesByCodes } from 'legacy/lut/components/widgets/public/onboarding/helpers/getSerializedNamesByCodes'
import { EMarket } from 'legacy/lut/configs/geo'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { lookupGetStateOld } from 'legacy/lut/store'
import { useEffect, useMemo, useState } from 'react'

import { Static } from 'core-components/types'
import { IDataProviderFilter, IFilter, Logic, Operator } from 'core-components/types'
import { Crud } from 'core/types/Crud'
import logger from 'core/utils/Logger'
import { IRequestorLA } from 'onboarding/classes/models/requestorLA'
import {
  FormCollectorCacheKey,
  onboardingFormCollectorApi,
} from 'onboarding/classes/services/api/OnboardingFormCollectorApi'
import { usePartnerTerritory } from 'onboarding/components/widgets/public/forms/hooks/usePartnerTerritory'
import { setIsDataUploadedForTransitionsMapping } from 'onboarding/features/onboardingSlice'
import { useParentRequest } from 'onboarding/hooks/useParentRequest'
import { useRequest } from 'onboarding/hooks/useRequest'
import { partnershipService } from 'onboarding/services/partnershipService'
import { requestorService } from 'onboarding/services/requestorService'

import { calculateFormCompleteness } from '../../helpers/calculateFormCompleteness'

const HPContractAdminLocation = [
  { label: 'HP Company', key: 'HPCompany' },
  { label: 'HP Local', key: 'HPLocal' },
]

const BuyerType = [
  { label: 'DAA - Broadline Distributor', key: 'DAA' },
  { label: 'DAB - Specialist Distributor', key: 'DAB' },
  { label: 'RAD - T1 Retailer', key: 'RAD' },
  { label: 'VAR - T1 Commercial Reseller', key: 'VAR' },
]

const SpecialPrograms = [
  { label: 'DAT', key: 'DAT' },
  { label: 'PIPP', key: 'PIPP' },
  { label: 'CIP', key: 'CIP' },
  { label: 'Scitex', key: 'Scitex' },
  { label: 'ASP', key: 'ASP' },
  { label: 'GSP', key: 'GSP' },
]

const mandatoryFields = ['HPContractAdminLocation', 'PartnerTerritoryISO', 'BuyerType', 'Exhibits']

export const useRequestorFillLAData = (
  setItem: (value: IRequestorLA) => void,
  requestId: number,
  hpContractParam: string,
  buyerTypeParam: string
) => {
  const [requestorLAWip, setRequestorLAWip] = useState(false)
  const [isSaving, setSaving] = useState(false)

  const dispatch = useAppDispatch()
  const { req, reqWip } = useRequest(requestId.toString())
  const { parentRequest, parentRequestWip } = useParentRequest(req.Parent)
  const requestWip = reqWip || parentRequestWip

  const request = useMemo(() => parentRequest ?? req, [req, parentRequest])

  const storeCountries = lookupGetStateOld().references.countries
  const laCountriesList = useMemo(() => {
    return storeCountries
      .filter((item: IObjectAny) => item.market === EMarket.latinAmerica && item.hporg)
      .map((item: IObjectAny) => item.value)
  }, [storeCountries])

  const partnerTerritory = usePartnerTerritory()

  useEffect(() => {
    if (request.EntryId) {
      setRequestorLAWip(true)
      requestorService
        .getLAByRequestId(request.EntryId, false)
        .then((el: IRequestorLA | null) => {
          el && setItem(el)
        })
        .catch(logger.error)
        .finally(() => {
          setRequestorLAWip(false)
        })
    }
  }, [setItem, request.EntryId])

  const cruds = useMemo(() => {
    return {
      HPContractAdminLocation: new Crud({
        fetcher: new Static(HPContractAdminLocation),
        keyField: 'key',
      }),
      BuyerType: new Crud({
        fetcher: new Static(BuyerType),
        keyField: 'key',
      }),
      SpecialPrograms: new Crud({
        fetcher: new Static(SpecialPrograms),
        keyField: 'key',
      }),
    }
  }, [])

  useEffect(() => {
    let filters: IDataProviderFilter[] | IFilter[] = []

    if (request.MainParameter === 'PATSY-D1_T1-Distributor') {
      filters = [
        {
          logic: Logic.OR,
          filters: [
            {
              field: 'key',
              operator: Operator.EQUAL,
              value: 'DAA',
            },
            {
              field: 'key',
              operator: Operator.EQUAL,
              value: 'DAB',
            },
          ],
        },
      ]
    } else if (request.MainParameter === 'PATSY-ST_T1-Retailer') {
      filters = [
        {
          field: 'key',
          operator: Operator.EQUAL,
          value: 'RAD',
        },
      ]
    } else if (request.MainParameter === 'PATSY-P1_T1-Reseller') {
      filters = [
        {
          field: 'key',
          operator: Operator.EQUAL,
          value: 'VAR',
        },
      ]
    }

    if (filters.length) {
      cruds.BuyerType.setFilter({
        logic: Logic.AND,
        filters: filters,
      })
    }
  }, [request.MainParameter])

  useEffect(() => {
    let filters: IDataProviderFilter[] | IFilter[] = []
    if (hpContractParam === 'HPCompany') {
      switch (buyerTypeParam) {
        case 'DAA':
        case 'DAB':
          filters = [
            {
              logic: Logic.OR,
              filters: [
                {
                  field: 'key',
                  operator: Operator.EQUAL,
                  value: 'DAT',
                },
                {
                  field: 'key',
                  operator: Operator.EQUAL,
                  value: 'PIPP',
                },
              ],
            },
          ]
          break
        case 'RAD':
          filters = [
            {
              field: 'key',
              operator: Operator.EQUAL,
              value: 'DAT',
            },
          ]
          break
        case 'VAR':
          filters = [
            {
              logic: Logic.OR,
              filters: [
                {
                  field: 'key',
                  operator: Operator.EQUAL,
                  value: 'DAT',
                },
                {
                  field: 'key',
                  operator: Operator.EQUAL,
                  value: 'PIPP',
                },
                {
                  field: 'key',
                  operator: Operator.EQUAL,
                  value: 'CIP',
                },
                {
                  field: 'key',
                  operator: Operator.EQUAL,
                  value: 'Scitex',
                },
              ],
            },
          ]
          break
      }
    } else if (hpContractParam === 'HPLocal') {
      switch (buyerTypeParam) {
        case 'DAA':
        case 'DAB':
          filters = [
            {
              field: 'key',
              operator: Operator.EQUAL,
              value: 'PIPP',
            },
          ]
          break
        case 'RAD':
          filters = [
            {
              field: 'key',
              operator: Operator.EQUAL,
              value: 'ASP',
            },
          ]
          break
        case 'VAR':
          filters = [
            {
              logic: Logic.OR,
              filters: [
                {
                  field: 'key',
                  operator: Operator.EQUAL,
                  value: 'CIP',
                },
                {
                  field: 'key',
                  operator: Operator.EQUAL,
                  value: 'GSP',
                },
                {
                  field: 'key',
                  operator: Operator.EQUAL,
                  value: 'PIPP',
                },
              ],
            },
          ]
          break
      }
    }

    if (filters.length) {
      cruds.SpecialPrograms.setFilter({
        logic: Logic.AND,
        filters: filters,
      })
    }
  }, [hpContractParam, buyerTypeParam])

  const onSave = async (item: Partial<IRequestorLA>) => {
    let newItem = { ...item }

    setSaving(true)
    newItem.PartnerTerritory = newItem.PartnerTerritoryISO
      ? getSerializedNamesByCodes(newItem.PartnerTerritoryISO, partnerTerritory.countries)
      : ''

    const formData = await requestorService.getLAByRequestId(request.EntryId, true)
    if (formData) {
      newItem = { ...formData, ...newItem }
    }
    newItem = {
      ...newItem,
      RequestId: request.EntryId,
      Completeness: calculateFormCompleteness(newItem, mandatoryFields),
    }

    if (newItem.EntryId) {
      const partnerTerritoryISO = (newItem.PartnerTerritoryISO || '')
        .split(',')
        .filter((item: string) => laCountriesList.includes(item))
        .join(',')

      const partnerTerritoryToSave = getSerializedNamesByCodes(
        partnerTerritoryISO,
        partnerTerritory.countries
      )

      const partnership = await partnershipService.getLAByRequestId(request.EntryId, true)
      if (!partnership || partnerTerritoryISO !== partnership.PartnerTerritory) {
        if (partnership) {
          await partnershipService.updateLA([
            {
              ...partnership,
              PartnerTerritory: partnerTerritoryToSave,
              PartnerTerritoryISO: partnerTerritoryISO,
            },
          ])
        } else {
          await partnershipService.createLA([
            {
              PartnerTerritory: partnerTerritoryToSave,
              PartnerTerritoryISO: partnerTerritoryISO,
              RequestId: request.EntryId,
            },
          ])
        }
      }
    }

    let saved
    if (formData) {
      saved = await requestorService.updateLA([newItem])
    } else {
      saved = await requestorService.createLA([newItem])
    }

    onboardingFormCollectorApi.triggerCrudOnChange(FormCollectorCacheKey.DataCollectorTable)
    dispatch(setIsDataUploadedForTransitionsMapping(true))

    setSaving(false)

    return saved[0] ?? null
  }

  return {
    cruds,
    requestorLAWip,
    requestWip,
    isSaving,
    onSave,
    mandatoryFields,
    partnerTerritory,
  }
}
